import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Building2, 
  Users, 
  FolderKanban, 
  Loader2, 
  Plus,
  Trash2,
  Edit,
  Eye,
  ShieldAlert,
  BarChart3
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

export default function AdminPage() {
  const { user, language } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTenant, setNewTenant] = useState({ id: '', name: '', subdomain: '' });
  const [creating, setCreating] = useState(false);

  const loadData = async () => {
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        adminApi.getTenants(),
        adminApi.getStats()
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel caricamento dati' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'super_admin') {
      loadData();
    }
  }, [user]);

  // Check if user is super_admin - must be AFTER all hooks
  if (user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleViewUsers = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const res = await adminApi.getTenantUsers(tenant.id);
      setTenantUsers(res.data);
      setShowUsersDialog(true);
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel caricamento utenti' : 'Failed to load users');
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.id || !newTenant.name) {
      toast.error(language === 'it' ? 'ID e Nome sono obbligatori' : 'ID and Name are required');
      return;
    }
    
    setCreating(true);
    try {
      await adminApi.createTenant(newTenant);
      toast.success(language === 'it' ? 'Tenant creato' : 'Tenant created');
      setShowCreateDialog(false);
      setNewTenant({ id: '', name: '', subdomain: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'it' ? 'Errore nella creazione' : 'Creation failed'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    try {
      await adminApi.deleteTenant(tenantId);
      toast.success(language === 'it' ? 'Tenant eliminato' : 'Tenant deleted');
      loadData();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'eliminazione' : 'Delete failed');
    }
  };

  const handleToggleActive = async (tenant) => {
    try {
      await adminApi.updateTenant(tenant.id, { is_active: !tenant.is_active });
      toast.success(language === 'it' ? 'Stato aggiornato' : 'Status updated');
      loadData();
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'aggiornamento' : 'Update failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-warning" />
            {language === 'it' ? 'Pannello Super Admin' : 'Super Admin Panel'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {language === 'it' ? 'Gestione tenant e statistiche piattaforma' : 'Tenant management and platform statistics'}
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          {language === 'it' ? 'Nuovo Tenant' : 'New Tenant'}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_tenants}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'it' ? 'Tenant Totali' : 'Total Tenants'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'it' ? 'Utenti Totali' : 'Total Users'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-info" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_projects}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'it' ? 'Progetti Totali' : 'Total Projects'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_experts}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'it' ? 'Esperti Eseguiti' : 'Experts Executed'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tenants Table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {language === 'it' ? 'Tenant' : 'Tenants'}
          </CardTitle>
          <CardDescription>
            {language === 'it' ? 'Gestisci i tenant della piattaforma' : 'Manage platform tenants'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>{language === 'it' ? 'Nome' : 'Name'}</TableHead>
                <TableHead>Subdomain</TableHead>
                <TableHead>{language === 'it' ? 'Utenti' : 'Users'}</TableHead>
                <TableHead>{language === 'it' ? 'Progetti' : 'Projects'}</TableHead>
                <TableHead>{language === 'it' ? 'Stato' : 'Status'}</TableHead>
                <TableHead>{language === 'it' ? 'Azioni' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map(tenant => (
                <TableRow key={tenant.id}>
                  <TableCell className="font-mono text-sm">{tenant.id}</TableCell>
                  <TableCell className="font-medium">{tenant.name}</TableCell>
                  <TableCell className="text-muted-foreground">{tenant.subdomain}</TableCell>
                  <TableCell>{tenant.user_count}</TableCell>
                  <TableCell>{tenant.project_count}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      tenant.is_active 
                        ? 'bg-success/20 text-success' 
                        : 'bg-destructive/20 text-destructive'
                    }`}>
                      {tenant.is_active 
                        ? (language === 'it' ? 'Attivo' : 'Active')
                        : (language === 'it' ? 'Disattivo' : 'Inactive')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleViewUsers(tenant)}
                        title={language === 'it' ? 'Vedi utenti' : 'View users'}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleToggleActive(tenant)}
                        title={language === 'it' ? 'Cambia stato' : 'Toggle status'}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {language === 'it' ? 'Eliminare il tenant?' : 'Delete tenant?'}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {language === 'it' 
                                ? `Questa azione eliminerà il tenant "${tenant.name}" e tutti i suoi dati (${tenant.user_count} utenti, ${tenant.project_count} progetti).`
                                : `This will delete tenant "${tenant.name}" and all its data (${tenant.user_count} users, ${tenant.project_count} projects).`}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>
                              {language === 'it' ? 'Annulla' : 'Cancel'}
                            </AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDeleteTenant(tenant.id)}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              {language === 'it' ? 'Elimina' : 'Delete'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    {language === 'it' ? 'Nessun tenant trovato' : 'No tenants found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>
              {language === 'it' ? 'Crea Nuovo Tenant' : 'Create New Tenant'}
            </DialogTitle>
            <DialogDescription>
              {language === 'it' 
                ? 'Inserisci i dettagli del nuovo tenant'
                : 'Enter the details for the new tenant'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>ID *</Label>
              <Input
                value={newTenant.id}
                onChange={(e) => setNewTenant(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s/g, '-') }))}
                placeholder="tenant-id"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label>{language === 'it' ? 'Nome' : 'Name'} *</Label>
              <Input
                value={newTenant.name}
                onChange={(e) => setNewTenant(prev => ({ ...prev, name: e.target.value }))}
                placeholder={language === 'it' ? 'Nome Azienda' : 'Company Name'}
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label>Subdomain</Label>
              <Input
                value={newTenant.subdomain}
                onChange={(e) => setNewTenant(prev => ({ ...prev, subdomain: e.target.value }))}
                placeholder={language === 'it' ? 'Opzionale' : 'Optional'}
                className="bg-background"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {language === 'it' ? 'Annulla' : 'Cancel'}
            </Button>
            <Button onClick={handleCreateTenant} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'it' ? 'Crea' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Users Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {language === 'it' ? 'Utenti di' : 'Users of'} {selectedTenant?.name}
            </DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>{language === 'it' ? 'Nome' : 'Name'}</TableHead>
                <TableHead>{language === 'it' ? 'Ruolo' : 'Role'}</TableHead>
                <TableHead>{language === 'it' ? 'Creato' : 'Created'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenantUsers.map(u => (
                <TableRow key={u.id}>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      u.role === 'tenant_admin' 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {u.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(u.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US')}
                  </TableCell>
                </TableRow>
              ))}
              {tenantUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                    {language === 'it' ? 'Nessun utente' : 'No users'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
